import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash,
  CalendarBlank,
  ListBullets,
  Table as TableIcon,
  GridFour,
  CaretLeft,
  CaretRight,
  Buildings,
  Minus,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAAND_NAMEN = [
  "Januari", "Februari", "Maart", "April", "Mei", "Juni",
  "Juli", "Augustus", "September", "Oktober", "November", "December",
];
const DAGNAMEN_KORT = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const ROOSTER_SLOTS = 5;

const getDagenVanMaand = (jaar, maand) => {
  const y = parseInt(jaar);
  const m = parseInt(maand);
  const last = new Date(y, m, 0).getDate();
  const arr = [];
  for (let d = 1; d <= last; d++) {
    const ds = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const wd = new Date(y, m - 1, d).getDay();
    const weekdag = (wd + 6) % 7; // 0=Ma..6=Zo
    arr.push({ datum: ds, dag: d, weekdag });
  }
  return arr;
};

// Geeft de maandag van de week waarin `dateStr` valt (YYYY-MM-DD).
const getMondayOf = (dateStr) => {
  const d = new Date(dateStr);
  const dow = (d.getDay() + 6) % 7; // 0=Ma..6=Zo
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
};

// Geeft 7 opeenvolgende dagen vanaf `mondayStr` (Ma t/m Zo).
const getDagenVanWeek = (mondayStr) => {
  const start = new Date(mondayStr);
  const arr = [];
  for (let i = 0; i < 7; i++) {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    arr.push({
      datum: dt.toISOString().slice(0, 10),
      dag: dt.getDate(),
      weekdag: i,
    });
  }
  return arr;
};

const shiftWeek = (mondayStr, weeks) => {
  const d = new Date(mondayStr);
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
};

const formatNL = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getDate()} ${MAAND_NAMEN[d.getMonth()].toLowerCase().slice(0, 3)}`;
};

export default function UrenInvoer() {
  const user = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "medewerker";
  const isMedewerker = role === "medewerker";

  const [mode, setMode] = useState("rooster"); // "dag" | "bulk" | "rooster"
  const [medewerkers, setMedewerkers] = useState([]);
  const [filialen, setFilialen] = useState([]);

  useEffect(() => {
    axios.get(`${API}/medewerkers`).then((r) => setMedewerkers(r.data || []));
    axios.get(`${API}/filialen`).then((r) => setFilialen(r.data || []));
  }, []);

  const visibleMedewerkers = isMedewerker
    ? medewerkers.filter((m) => m.id === user?.id)
    : medewerkers;

  // ── 1-Dag form ──
  const [dagForm, setDagForm] = useState({
    medewerker_id: "",
    filiaal_id: "",
    datum: new Date().toISOString().slice(0, 10),
    uren: "",
  });
  const [dagSaving, setDagSaving] = useState(false);

  // Generieke confirm-dialog voor "andere filiaal bestaat al" (gebruikt door 1 Dag + Periode)
  const [confirmDup, setConfirmDup] = useState(null);
  // shape: { titel, intro, existing: [{datum, filiaal_naam, uren}], onConfirm }

  // Helper: laad bestaande records voor een medewerker in een datum-range
  const fetchExisting = async (medewerker_id, datum_van, datum_tot) => {
    const res = await axios.get(`${API}/aanwezigheid`, {
      params: { datum_van, datum_tot },
    });
    return (res.data || []).filter((r) => r.medewerker_id === medewerker_id);
  };

  const performDagSave = async (f) => {
    setDagSaving(true);
    try {
      await axios.post(`${API}/aanwezigheid/handmatig`, {
        medewerker_id: f.medewerker_id,
        filiaal_id: f.filiaal_id,
        datum: f.datum,
        uren: parseFloat(f.uren),
      });
      toast.success("Uren toegevoegd");
      setDagForm({ ...f, uren: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    } finally {
      setDagSaving(false);
    }
  };

  const handleDagSave = async () => {
    const f = dagForm;
    if (!f.medewerker_id || !f.filiaal_id || !f.datum || !f.uren) {
      toast.error("Vul alle velden in");
      return;
    }
    // Check op bestaande andere filialen op dezelfde dag
    try {
      const existing = await fetchExisting(f.medewerker_id, f.datum, f.datum);
      const conflicts = existing.filter(
        (r) => r.filiaal_id !== f.filiaal_id && (parseFloat(r.uren) || 0) > 0,
      );
      if (conflicts.length > 0) {
        setConfirmDup({
          titel: "Extra filiaal op deze dag?",
          intro: `Op ${f.datum} staan al uren ingevuld voor een ander filiaal voor deze medewerker. Wil je toch ook ${parseFloat(f.uren)} u toevoegen voor het gekozen filiaal?`,
          existing: conflicts.map((r) => ({
            datum: r.datum,
            filiaal_naam: r.filiaal_naam,
            uren: parseFloat(r.uren) || 0,
          })),
          onConfirm: () => performDagSave(f),
        });
        return;
      }
    } catch {
      // bij netwerkfout gewoon doorgaan (geen blokker)
    }
    await performDagSave(f);
  };

  // ── Periode form ──
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 7) + "-01";
  const [bulkForm, setBulkForm] = useState({
    medewerker_id: "",
    filiaal_id: "",
    datum_van: firstOfMonth,
    datum_tot: today,
    weekdagen: [0, 1, 2, 3, 4],
    uren: "8",
    overschrijven: false,
  });
  const [bulkSaving, setBulkSaving] = useState(false);

  const toggleWeekdag = (idx) => {
    setBulkForm((b) => ({
      ...b,
      weekdagen: b.weekdagen.includes(idx)
        ? b.weekdagen.filter((x) => x !== idx)
        : [...b.weekdagen, idx].sort(),
    }));
  };

  const performBulkSave = async (b) => {
    setBulkSaving(true);
    try {
      const res = await axios.post(`${API}/aanwezigheid/handmatig/bulk`, {
        medewerker_id: b.medewerker_id,
        filiaal_id: b.filiaal_id,
        datum_van: b.datum_van,
        datum_tot: b.datum_tot,
        weekdagen: b.weekdagen,
        uren: parseFloat(b.uren),
        overschrijven: b.overschrijven,
      });
      const d = res.data || {};
      const parts = [];
      if (d.aangemaakt) parts.push(`${d.aangemaakt} nieuw`);
      if (d.overschreven) parts.push(`${d.overschreven} bijgewerkt`);
      if (d.overgeslagen_bestaat)
        parts.push(`${d.overgeslagen_bestaat} overgeslagen`);
      toast.success(parts.length ? parts.join(" · ") : "Opgeslagen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkSave = async () => {
    const b = bulkForm;
    if (!b.medewerker_id || !b.filiaal_id || !b.datum_van || !b.datum_tot || !b.uren) {
      toast.error("Vul alle velden in");
      return;
    }
    if (b.weekdagen.length === 0) {
      toast.error("Selecteer minstens één weekdag");
      return;
    }
    // Check op bestaande andere filialen in de range op de geselecteerde weekdagen
    try {
      const existing = await fetchExisting(b.medewerker_id, b.datum_van, b.datum_tot);
      const conflicts = existing.filter((r) => {
        if (r.filiaal_id === b.filiaal_id) return false;
        if ((parseFloat(r.uren) || 0) <= 0) return false;
        const wd = (new Date(r.datum).getDay() + 6) % 7;
        return b.weekdagen.includes(wd);
      });
      if (conflicts.length > 0) {
        setConfirmDup({
          titel: `Extra filiaal voor ${conflicts.length} dag${conflicts.length === 1 ? "" : "en"}?`,
          intro: `In deze periode staan al uren ingevuld voor een ander filiaal op ${conflicts.length} dag${conflicts.length === 1 ? "" : "en"}. Wil je toch het gekozen filiaal toevoegen voor elke dag?`,
          existing: conflicts.slice(0, 8).map((r) => ({
            datum: r.datum,
            filiaal_naam: r.filiaal_naam,
            uren: parseFloat(r.uren) || 0,
          })),
          extraCount: Math.max(0, conflicts.length - 8),
          onConfirm: () => performBulkSave(b),
        });
        return;
      }
    } catch {
      /* netwerk-fout: gewoon doorgaan */
    }
    await performBulkSave(b);
  };

  // ── Maandrooster ──
  const now = new Date();
  const [rMaand, setRMaand] = useState(String(now.getMonth() + 1));
  const [rJaar, setRJaar] = useState(String(now.getFullYear()));
  const [rMedewerker, setRMedewerker] = useState("");
  const [rFiliaal, setRFiliaal] = useState("");
  const [rDagen, setRDagen] = useState({}); // {datum: [{filiaal_id, uren}, ...]}

  // Filiaal-filter: medewerkers van dat filiaal bovenaan, de rest eronder
  const filiaalMedewerkers = rFiliaal
    ? visibleMedewerkers.filter((m) => (m.filiaal_ids || []).includes(rFiliaal))
    : visibleMedewerkers;
  const andereMedewerkers = rFiliaal
    ? visibleMedewerkers.filter((m) => !(m.filiaal_ids || []).includes(rFiliaal))
    : [];

  const gekozenRMedewerker = medewerkers.find((m) => m.id === rMedewerker);
  const mwFilialen = gekozenRMedewerker?.filiaal_ids || [];
  const standaardFiliaal = rFiliaal || (mwFilialen.length === 1 ? mwFilialen[0] : "");
  const [rSaving, setRSaving] = useState(false);
  // Confirm dialog state voor "extra filiaal terwijl al uren gevuld"
  const [confirmExtra, setConfirmExtra] = useState(null); // {datum, slotIdx, filiaal_id, existing}

  // Auto-load bestaande uren wanneer medewerker / maand / jaar verandert
  useEffect(() => {
    if (mode !== "rooster" || !rMedewerker) return;
    let cancelled = false;
    const y = parseInt(rJaar);
    const m = parseInt(rMaand);
    const ds_van = `${y}-${String(m).padStart(2, "0")}-01`;
    const last = new Date(y, m, 0).getDate();
    const ds_tot = `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    (async () => {
      try {
        const res = await axios.get(`${API}/aanwezigheid`, {
          params: { datum_van: ds_van, datum_tot: ds_tot },
        });
        if (cancelled) return;
        const mine = (res.data || []).filter((r) => r.medewerker_id === rMedewerker);
        const map = {};
        mine.forEach((r) => {
          if (!map[r.datum]) map[r.datum] = [];
          if (map[r.datum].length < ROOSTER_SLOTS) {
            map[r.datum].push({
              filiaal_id: r.filiaal_id || "",
              uren: r.uren ? String(r.uren) : "",
            });
          }
        });
        setRDagen(map);
      } catch {
        /* stil falen */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, rMedewerker, rMaand, rJaar]);

  const setSlot = (datum, idx, field, value) => {
    // Uren invullen in een leeg slot: filiaal automatisch meenemen
    if (field === "uren" && value) {
      const huidig = (rDagen[datum] || [])[idx];
      if (!huidig?.filiaal_id && standaardFiliaal) {
        setRDagen((prev) => {
          const cur = prev[datum] ? [...prev[datum]] : [];
          while (cur.length <= idx) cur.push({ filiaal_id: "", uren: "" });
          cur[idx] = { filiaal_id: standaardFiliaal, uren: value };
          return { ...prev, [datum]: cur };
        });
        return;
      }
    }
    // Bij wijzigen van filiaal in een nog-leeg slot, en er staan al gevulde slots
    // op dezelfde dag → vraag bevestiging
    if (field === "filiaal_id" && value) {
      const slots = rDagen[datum] || [];
      const wasEmpty = !slots[idx]?.filiaal_id;
      const others = slots.filter((s, i) => i !== idx && s.filiaal_id && parseFloat(s.uren) > 0);
      if (wasEmpty && others.length > 0) {
        setConfirmExtra({
          datum,
          slotIdx: idx,
          filiaal_id: value,
          existing: others.map((s) => ({
            filiaal_naam: filialen.find((f) => f.id === s.filiaal_id)?.naam || "?",
            uren: parseFloat(s.uren) || 0,
          })),
        });
        return; // wacht op bevestiging
      }
    }
    setRDagen((prev) => {
      const cur = prev[datum] ? [...prev[datum]] : [];
      while (cur.length <= idx) cur.push({ filiaal_id: "", uren: "" });
      cur[idx] = { ...cur[idx], [field]: value };
      return { ...prev, [datum]: cur };
    });
  };

  const applyConfirmedSlot = () => {
    if (!confirmExtra) return;
    const { datum, slotIdx, filiaal_id } = confirmExtra;
    setRDagen((prev) => {
      const cur = prev[datum] ? [...prev[datum]] : [];
      while (cur.length <= slotIdx) cur.push({ filiaal_id: "", uren: "" });
      cur[slotIdx] = { ...cur[slotIdx], filiaal_id };
      return { ...prev, [datum]: cur };
    });
    setConfirmExtra(null);
  };

  const removeSlot = (datum, idx) => {
    setRDagen((prev) => {
      const cur = prev[datum] ? [...prev[datum]] : [];
      cur.splice(idx, 1);
      const next = { ...prev };
      if (cur.length === 0) delete next[datum];
      else next[datum] = cur;
      return next;
    });
  };

  const vulAllesMaVr = (filiaalId, uren) => {
    if (!filiaalId || !uren) return;
    const dagen = getDagenVanMaand(rJaar, rMaand);
    setRDagen((prev) => {
      const next = { ...prev };
      dagen.forEach(({ datum, weekdag }) => {
        if (weekdag >= 5) return;
        next[datum] = [{ filiaal_id: filiaalId, uren: String(uren) }];
      });
      return next;
    });
    toast.success("Ma-Vr ingevuld");
  };

  const clearRooster = () => {
    if (Object.keys(rDagen).length === 0) return;
    if (!window.confirm("Alle ingevoerde uren in dit scherm wissen?")) return;
    setRDagen({});
  };

  const saveRooster = async () => {
    if (!rMedewerker) {
      toast.error("Selecteer een medewerker");
      return;
    }
    const entries = [];
    Object.entries(rDagen).forEach(([datum, slots]) => {
      slots.forEach((s, i) => {
        const u = parseFloat(s.uren);
        const fid = s.filiaal_id || (i === 0 ? standaardFiliaal : "");
        if (fid && isFinite(u) && u > 0) {
          entries.push({ datum, filiaal_id: fid, uren: u });
        }
      });
    });
    if (entries.length === 0) {
      toast.error("Geen dagen ingevuld");
      return;
    }
    setRSaving(true);
    try {
      const res = await axios.post(`${API}/aanwezigheid/handmatig/rooster`, {
        medewerker_id: rMedewerker,
        entries,
        overschrijven: true,
      });
      const d = res.data || {};
      const parts = [];
      if (d.aangemaakt) parts.push(`${d.aangemaakt} nieuw`);
      if (d.overschreven) parts.push(`${d.overschreven} bijgewerkt`);
      toast.success(parts.length ? parts.join(" · ") : "Opgeslagen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    } finally {
      setRSaving(false);
    }
  };

  // ── Filiaal invoer (1 dag, meerdere medewerkers) ──
  const magDatumKiezen = ["admin", "superadmin"].includes(role);
  const vandaagStr = new Date().toISOString().slice(0, 10);
  const [fFiliaal, setFFiliaal] = useState("");
  const [fDatum, setFDatum] = useState(vandaagStr);
  const [fUren, setFUren] = useState({});
  const [fBestaand, setFBestaand] = useState({});
  const [fSaving, setFSaving] = useState(false);
  const [fLaden, setFLaden] = useState(false);

  const fFiliaalMedewerkers = fFiliaal
    ? visibleMedewerkers.filter((m) => (m.filiaal_ids || []).includes(fFiliaal))
    : [];
  const fAndereMedewerkers = fFiliaal
    ? visibleMedewerkers.filter((m) => !(m.filiaal_ids || []).includes(fFiliaal))
    : [];

  useEffect(() => {
    if (mode !== "filiaal" || !fFiliaal || !fDatum) return;
    let cancelled = false;
    setFLaden(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/aanwezigheid`, {
          params: { datum_van: fDatum, datum_tot: fDatum },
        });
        if (cancelled) return;
        const map = {};
        (res.data || []).forEach((r) => {
          if (r.filiaal_id === fFiliaal && r.uren) map[r.medewerker_id] = String(r.uren);
        });
        setFUren(map);
        setFBestaand(map);
      } catch {
        /* stil falen */
      } finally {
        if (!cancelled) setFLaden(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, fFiliaal, fDatum]);

  const urenGetal = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;

  const formatUurMin = (num) => {
    if (!num) return "0u";
    const uren = Math.floor(num);
    const min = Math.round((num - uren) * 60);
    if (uren && min) return `${uren}u ${min}m`;
    if (uren) return `${uren}u`;
    return `${min}m`;
  };

  const stapUren = (id, delta) => {
    const nieuw = Math.min(24, Math.max(0, Math.round((urenGetal(fUren[id]) + delta) * 4) / 4));
    setFUren((prev) => ({ ...prev, [id]: nieuw === 0 ? "0" : String(nieuw) }));
  };

  const fTotaal = Object.values(fUren).reduce(
    (s, v) => s + (parseFloat(String(v).replace(",", ".")) || 0), 0,
  );
  const fAantal = Object.values(fUren).filter(
    (v) => (parseFloat(String(v).replace(",", ".")) || 0) > 0,
  ).length;

  const saveFiliaalDag = async () => {
    if (!fFiliaal || !fDatum) {
      toast.error("Kies een filiaal en datum");
      return;
    }
    const items = [];
    const ids = new Set([...Object.keys(fUren), ...Object.keys(fBestaand)]);
    ids.forEach((id) => {
      const raw = fUren[id];
      const uren = parseFloat(String(raw ?? "").replace(",", ".")) || 0;
      if (uren > 0 || fBestaand[id]) items.push({ medewerker_id: id, uren });
    });
    if (items.length === 0) {
      toast.error("Vul minstens bij één medewerker uren in");
      return;
    }
    const filiaalNaam = filialen.find((f) => f.id === fFiliaal)?.naam || "dit filiaal";
    const bevestig = window.confirm(
      `Uren opslaan voor ${filiaalNaam} op ${fDatum}?\n\n` +
      `${fAantal} medewerker${fAantal === 1 ? "" : "s"} · ${formatUurMin(fTotaal)} totaal`,
    );
    if (!bevestig) return;
    setFSaving(true);
    try {
      const res = await axios.post(`${API}/aanwezigheid/handmatig/filiaal-dag`, {
        filiaal_id: fFiliaal,
        datum: fDatum,
        items,
      });
      const d = res.data || {};
      const parts = [];
      if (d.aangemaakt) parts.push(`${d.aangemaakt} nieuw`);
      if (d.bijgewerkt) parts.push(`${d.bijgewerkt} bijgewerkt`);
      if (d.verwijderd) parts.push(`${d.verwijderd} gewist`);
      toast.success(parts.length ? parts.join(" · ") : "Opgeslagen");
      setFBestaand({ ...fUren });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    } finally {
      setFSaving(false);
    }
  };

  // ── Quick-fill modal ──
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickFiliaal, setQuickFiliaal] = useState("");
  const [quickUren, setQuickUren] = useState("8");

  useEffect(() => {
    if (standaardFiliaal) setQuickFiliaal(standaardFiliaal);
  }, [standaardFiliaal]);

  const dagenVanMaand = getDagenVanMaand(rJaar, rMaand);
  const totaalMaand = Object.values(rDagen)
    .flat()
    .reduce((s, x) => s + (parseFloat(x.uren) || 0), 0);
  const aantalDagen = Object.values(rDagen).filter(
    (arr) => arr.some((s) => s.filiaal_id && parseFloat(s.uren) > 0)
  ).length;

  // ═══════════════════════════════════════════════════════════════════
  // ── MATRIX (multi-medewerker × dagen) ──
  // ═══════════════════════════════════════════════════════════════════
  const [mxPeriode, setMxPeriode] = useState("week"); // "week" | "maand"
  const [mxWeekStart, setMxWeekStart] = useState(getMondayOf(today));
  const [mxMaand, setMxMaand] = useState(String(now.getMonth() + 1));
  const [mxJaar, setMxJaar] = useState(String(now.getFullYear()));
  // rij = (medewerker_id, filiaal_id) combo
  // mxRijen: [{rowId, medewerker_id, filiaal_id, uren: {datum: "8"}}]
  const [mxRijen, setMxRijen] = useState([]);
  const [mxLoading, setMxLoading] = useState(false);
  const [mxSaving, setMxSaving] = useState(false);

  const mxDagen =
    mxPeriode === "week"
      ? getDagenVanWeek(mxWeekStart)
      : getDagenVanMaand(mxJaar, mxMaand);

  // Auto-load bestaande uren
  const loadMatrix = useCallback(async () => {
    if (mxDagen.length === 0) return;
    setMxLoading(true);
    try {
      const datum_van = mxDagen[0].datum;
      const datum_tot = mxDagen[mxDagen.length - 1].datum;
      const res = await axios.get(`${API}/aanwezigheid`, {
        params: { datum_van, datum_tot },
      });
      const records = res.data || [];
      // Groep per (medewerker_id, filiaal_id)
      const groupMap = new Map();
      records.forEach((r) => {
        if (!r.medewerker_id || !r.filiaal_id) return;
        if (isMedewerker && r.medewerker_id !== user?.id) return;
        const key = `${r.medewerker_id}::${r.filiaal_id}`;
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            rowId: key,
            medewerker_id: r.medewerker_id,
            filiaal_id: r.filiaal_id,
            uren: {},
          });
        }
        const u = parseFloat(r.uren) || 0;
        if (u > 0) groupMap.get(key).uren[r.datum] = String(u);
      });
      // Voor elke zichtbare medewerker zonder rij → toevoegen met leeg filiaal
      visibleMedewerkers.forEach((m) => {
        const heeft = Array.from(groupMap.keys()).some((k) =>
          k.startsWith(`${m.id}::`),
        );
        if (!heeft) {
          const key = `${m.id}::`;
          groupMap.set(key, {
            rowId: key,
            medewerker_id: m.id,
            filiaal_id: "",
            uren: {},
          });
        }
      });
      setMxRijen(Array.from(groupMap.values()));
    } catch {
      toast.error("Kon bestaande uren niet laden");
    } finally {
      setMxLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mxPeriode, mxWeekStart, mxMaand, mxJaar, medewerkers.length, isMedewerker]);

  useEffect(() => {
    if (mode === "matrix" && medewerkers.length > 0) loadMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, mxPeriode, mxWeekStart, mxMaand, mxJaar, medewerkers.length]);

  const setMxCel = (rowId, datum, value) => {
    setMxRijen((prev) =>
      prev.map((r) =>
        r.rowId === rowId
          ? { ...r, uren: { ...r.uren, [datum]: value } }
          : r,
      ),
    );
  };

  const setMxFiliaal = (rowId, filiaal_id) => {
    setMxRijen((prev) => {
      // hernoem rowId zodat de key blijft consistent
      return prev.map((r) =>
        r.rowId === rowId
          ? { ...r, filiaal_id, rowId: `${r.medewerker_id}::${filiaal_id}` }
          : r,
      );
    });
  };

  const addMxRij = (medewerker_id) => {
    if (!medewerker_id) return;
    const newKey = `${medewerker_id}::__new_${Date.now()}`;
    setMxRijen((prev) => [
      ...prev,
      { rowId: newKey, medewerker_id, filiaal_id: "", uren: {} },
    ]);
  };

  const removeMxRij = (rowId) => {
    setMxRijen((prev) => prev.filter((r) => r.rowId !== rowId));
  };

  const fillMxRowMaVr = (rowId, uren) => {
    if (!uren) return;
    setMxRijen((prev) =>
      prev.map((r) => {
        if (r.rowId !== rowId) return r;
        const next = { ...r.uren };
        mxDagen.forEach(({ datum, weekdag }) => {
          if (weekdag < 5) next[datum] = String(uren);
        });
        return { ...r, uren: next };
      }),
    );
  };

  const clearMxRow = (rowId) => {
    setMxRijen((prev) =>
      prev.map((r) => (r.rowId === rowId ? { ...r, uren: {} } : r)),
    );
  };

  const totaalRij = (r) =>
    Object.values(r.uren).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const totaalKolom = (datum) =>
    mxRijen.reduce((s, r) => s + (parseFloat(r.uren[datum]) || 0), 0);

  const totaalMatrix = mxRijen.reduce((s, r) => s + totaalRij(r), 0);

  const saveMatrix = async () => {
    // Verzamel entries per medewerker
    const perMed = new Map();
    let invalidFiliaal = 0;
    for (const r of mxRijen) {
      const entries = [];
      Object.entries(r.uren).forEach(([datum, v]) => {
        const u = parseFloat(v);
        if (isFinite(u) && u > 0) entries.push({ datum, uren: u });
      });
      if (entries.length === 0) continue;
      if (!r.filiaal_id) {
        invalidFiliaal += entries.length;
        continue;
      }
      if (!perMed.has(r.medewerker_id)) perMed.set(r.medewerker_id, []);
      entries.forEach((e) =>
        perMed.get(r.medewerker_id).push({ ...e, filiaal_id: r.filiaal_id }),
      );
    }
    if (perMed.size === 0) {
      toast.error(
        invalidFiliaal > 0
          ? "Selecteer een filiaal voor de ingevulde rijen"
          : "Niets om op te slaan",
      );
      return;
    }
    setMxSaving(true);
    let totAan = 0,
      totOver = 0,
      fout = 0;
    for (const [medewerker_id, entries] of perMed.entries()) {
      try {
        const res = await axios.post(`${API}/aanwezigheid/handmatig/rooster`, {
          medewerker_id,
          entries,
          overschrijven: true,
        });
        totAan += res.data?.aangemaakt || 0;
        totOver += res.data?.overschreven || 0;
      } catch {
        fout += 1;
      }
    }
    setMxSaving(false);
    if (fout > 0) {
      toast.error(`${fout} medewerker(s) faalden bij opslaan`);
    } else {
      const parts = [];
      if (totAan) parts.push(`${totAan} nieuw`);
      if (totOver) parts.push(`${totOver} bijgewerkt`);
      if (invalidFiliaal)
        parts.push(`${invalidFiliaal} cel(len) zonder filiaal genegeerd`);
      toast.success(parts.length ? parts.join(" · ") : "Opgeslagen");
      loadMatrix();
    }
  };

  const periodeLabel =
    mxPeriode === "week"
      ? `${formatNL(mxDagen[0].datum)} – ${formatNL(mxDagen[6].datum)}`
      : `${MAAND_NAMEN[parseInt(mxMaand) - 1]} ${mxJaar}`;

  return (
    <div className="space-y-5" data-testid="uren-invoer-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
            title="Terug"
          >
            <ArrowLeft size={18} weight="bold" />
          </Button>
          <div>
            <h1
              className="text-2xl font-black tracking-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Handmatige uren invoer
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Voeg uren toe voor 1 dag, een periode, of bouw een volledig maandrooster.
            </p>
          </div>
        </div>
        {mode === "filiaal" && (
          <Button
            onClick={saveFiliaalDag}
            disabled={fSaving || !fFiliaal}
            className="bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="fd-save-top"
          >
            <Plus size={16} weight="bold" className="mr-1" />
            {fSaving ? "Opslaan..." : "Opslaan"}
          </Button>
        )}
        {mode === "rooster" && (
          <Button
            onClick={saveRooster}
            disabled={rSaving}
            className="bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="rooster-save-top"
          >
            <Plus size={16} weight="bold" className="mr-1" />
            {rSaving ? "Opslaan..." : `Maand opslaan (${MAAND_NAMEN[parseInt(rMaand) - 1]} ${rJaar})`}
          </Button>
        )}
        {mode === "dag" && (
          <Button
            onClick={handleDagSave}
            disabled={dagSaving}
            className="bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="dag-save-top"
          >
            <Plus size={16} weight="bold" className="mr-1" />
            {dagSaving ? "Opslaan..." : "Uren toevoegen"}
          </Button>
        )}
        {mode === "bulk" && (
          <Button
            onClick={handleBulkSave}
            disabled={bulkSaving}
            className="bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="bulk-save-top"
          >
            <Plus size={16} weight="bold" className="mr-1" />
            {bulkSaving ? "Bezig..." : "Bulk toevoegen"}
          </Button>
        )}
        {mode === "matrix" && (
          <Button
            onClick={saveMatrix}
            disabled={mxSaving}
            className="bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="matrix-save-top"
          >
            <Plus size={16} weight="bold" className="mr-1" />
            {mxSaving ? "Opslaan..." : `Matrix opslaan (${periodeLabel})`}
          </Button>
        )}
      </div>

      {/* Mode tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1 p-1 bg-muted rounded-sm max-w-3xl" data-testid="mode-tabs">
        <button
          onClick={() => setMode("filiaal")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            mode === "filiaal" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="tab-filiaal"
        >
          <Buildings size={14} weight="bold" /> Filiaal invoer
        </button>
        <button
          onClick={() => setMode("dag")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            mode === "dag" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="tab-dag"
        >
          <CalendarBlank size={14} weight="bold" /> 1 Dag
        </button>
        <button
          onClick={() => setMode("bulk")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            mode === "bulk" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="tab-bulk"
        >
          <ListBullets size={14} weight="bold" /> Periode
        </button>
        <button
          onClick={() => setMode("rooster")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            mode === "rooster" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="tab-rooster"
        >
          <TableIcon size={14} weight="bold" /> 1 Medewerker
        </button>
        <button
          onClick={() => setMode("matrix")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            mode === "matrix" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="tab-matrix"
        >
          <GridFour size={14} weight="bold" /> Matrix
        </button>
      </div>

      {/* ═══ Filiaal invoer ═══ */}
      {mode === "filiaal" && (
        <div className="bg-white border border-border rounded-sm p-5" data-testid="form-filiaal">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mb-4">
            <div>
              <Label className="text-xs font-semibold">Filiaal</Label>
              <Select value={fFiliaal} onValueChange={setFFiliaal}>
                <SelectTrigger className="mt-1.5" data-testid="fd-filiaal"><SelectValue placeholder="Kies filiaal..." /></SelectTrigger>
                <SelectContent>
                  {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Datum</Label>
              <Input
                type="date"
                value={fDatum}
                onChange={(e) => setFDatum(e.target.value)}
                disabled={!magDatumKiezen}
                className="mt-1.5"
                data-testid="fd-datum"
              />
              {!magDatumKiezen && (
                <p className="text-[10px] text-muted-foreground mt-1" data-testid="fd-datum-vast">
                  Je kan enkel uren van vandaag invoeren
                </p>
              )}
            </div>
          </div>

          {!fFiliaal ? (
            <p className="text-sm text-muted-foreground" data-testid="fd-geen-filiaal">
              Kies eerst een filiaal om de medewerkers te zien.
            </p>
          ) : (
            <>
              <div className="border border-border rounded-sm overflow-hidden">
                {[
                  { label: filialen.find((f) => f.id === fFiliaal)?.naam || "Filiaal", lijst: fFiliaalMedewerkers },
                  { label: "Andere medewerkers", lijst: fAndereMedewerkers },
                ].map((groep) => groep.lijst.length === 0 ? null : (
                  <Fragment key={groep.label}>
                    <div className="px-3 py-1.5 bg-[#EAF1FF] text-[10px] font-bold uppercase tracking-wider text-[#002FA7]">
                      {groep.label}
                    </div>
                    {groep.lijst.map((m) => {
                      const num = urenGetal(fUren[m.id]);
                      return (
                        <div
                          key={m.id}
                          className={`flex items-center justify-between gap-3 px-3 py-2.5 border-t border-border ${num > 0 ? "bg-[#F0FAEC]" : ""}`}
                          data-testid={`fd-rij-${m.id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{m.voornaam} {m.achternaam}</p>
                            <p className="text-[11px] text-muted-foreground tabular-nums" data-testid={`fd-label-${m.id}`}>
                              {formatUurMin(num)}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => stapUren(m.id, -0.25)}
                              className="h-11 w-11 rounded-sm border border-border flex items-center justify-center text-lg font-black text-[#002FA7] active:bg-muted disabled:opacity-40"
                              disabled={num <= 0}
                              data-testid={`fd-min-${m.id}`}
                              aria-label="15 minuten minder"
                            >
                              <Minus size={18} weight="bold" />
                            </button>
                            <Input
                              type="number"
                              step="0.25"
                              min="0"
                              max="24"
                              inputMode="decimal"
                              value={fUren[m.id] ?? ""}
                              onChange={(e) => setFUren({ ...fUren, [m.id]: e.target.value })}
                              placeholder="0"
                              className="h-11 w-20 text-center text-base font-bold tabular-nums"
                              data-testid={`fd-uren-${m.id}`}
                            />
                            <button
                              type="button"
                              onClick={() => stapUren(m.id, 0.25)}
                              className="h-11 w-11 rounded-sm bg-[#002FA7] text-white flex items-center justify-center active:bg-[#002280] disabled:opacity-40"
                              disabled={num >= 24}
                              data-testid={`fd-plus-${m.id}`}
                              aria-label="15 minuten meer"
                            >
                              <Plus size={18} weight="bold" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
                <div className="flex items-center justify-between gap-3 px-3 py-3 bg-[#EAF1FF] border-t-2 border-[#002FA7]">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#002FA7]">
                    {fAantal} medewerker{fAantal === 1 ? "" : "s"} met uren
                  </p>
                  <p className="text-base font-black tabular-nums text-[#002FA7]" data-testid="fd-totaal">
                    {formatUurMin(fTotaal)}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Gebruik de pijlen voor stappen van 15 min. Leeg of 0 wist bestaande uren van die dag voor dit filiaal.
                  {fLaden && " · bestaande uren laden..."}
                </p>
                <Button
                  onClick={saveFiliaalDag}
                  disabled={fSaving}
                  className="bg-[#002FA7] hover:bg-[#002280] text-white h-12 sm:h-10 w-full sm:w-auto"
                  data-testid="fd-save"
                >
                  <Plus size={16} weight="bold" className="mr-1" />
                  {fSaving ? "Opslaan..." : "Opslaan"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ 1-Dag ═══ */}
      {mode === "dag" && (
        <div className="bg-white border border-border rounded-sm p-5 max-w-xl" data-testid="form-dag">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4">
            Uren voor 1 dag
          </h2>
          <div className="grid gap-4">
            <div>
              <Label className="text-xs font-semibold">Medewerker</Label>
              <Select value={dagForm.medewerker_id} onValueChange={(v) => setDagForm({ ...dagForm, medewerker_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="dag-medewerker">
                  <SelectValue placeholder="Selecteer..." />
                </SelectTrigger>
                <SelectContent>
                  {visibleMedewerkers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Filiaal</Label>
              <Select value={dagForm.filiaal_id} onValueChange={(v) => setDagForm({ ...dagForm, filiaal_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="dag-filiaal">
                  <SelectValue placeholder="Selecteer..." />
                </SelectTrigger>
                <SelectContent>
                  {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Datum</Label>
                <Input type="date" value={dagForm.datum} onChange={(e) => setDagForm({ ...dagForm, datum: e.target.value })} className="mt-1.5" data-testid="dag-datum" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Uren</Label>
                <Input type="number" step="0.25" min="0" max="24" value={dagForm.uren} onChange={(e) => setDagForm({ ...dagForm, uren: e.target.value })} placeholder="8" className="mt-1.5" data-testid="dag-uren" />
              </div>
            </div>
            <Button
              onClick={handleDagSave}
              disabled={dagSaving}
              className="bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="dag-save"
            >
              <Plus size={16} weight="bold" className="mr-1" />
              {dagSaving ? "Opslaan..." : "Uren toevoegen"}
            </Button>
          </div>
        </div>
      )}

      {/* ═══ Periode ═══ */}
      {mode === "bulk" && (
        <div className="bg-white border border-border rounded-sm p-5 max-w-xl" data-testid="form-bulk">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4">
            Uren voor een periode
          </h2>
          <div className="grid gap-4">
            <div>
              <Label className="text-xs font-semibold">Medewerker</Label>
              <Select value={bulkForm.medewerker_id} onValueChange={(v) => setBulkForm({ ...bulkForm, medewerker_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="bulk-medewerker"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                <SelectContent>
                  {visibleMedewerkers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Filiaal</Label>
              <Select value={bulkForm.filiaal_id} onValueChange={(v) => setBulkForm({ ...bulkForm, filiaal_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="bulk-filiaal"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                <SelectContent>
                  {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Datum van</Label>
                <Input type="date" value={bulkForm.datum_van} onChange={(e) => setBulkForm({ ...bulkForm, datum_van: e.target.value })} className="mt-1.5" data-testid="bulk-datum-van" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Datum tot</Label>
                <Input type="date" value={bulkForm.datum_tot} onChange={(e) => setBulkForm({ ...bulkForm, datum_tot: e.target.value })} className="mt-1.5" data-testid="bulk-datum-tot" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Werkdagen</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="bulk-weekdagen">
                {DAGNAMEN_KORT.map((naam, idx) => {
                  const active = bulkForm.weekdagen.includes(idx);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleWeekdag(idx)}
                      className={`flex-1 min-w-[42px] py-2 text-xs font-bold rounded-sm border transition-colors ${
                        active
                          ? "bg-[#002FA7] text-white border-[#002FA7]"
                          : "bg-white text-muted-foreground border-border hover:border-[#002FA7]"
                      }`}
                      data-testid={`bulk-weekdag-${idx}`}
                    >
                      {naam}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Uren per dag</Label>
              <Input type="number" step="0.25" min="0" max="24" value={bulkForm.uren} onChange={(e) => setBulkForm({ ...bulkForm, uren: e.target.value })} placeholder="8" className="mt-1.5" data-testid="bulk-uren" />
            </div>
            <div className="flex items-center gap-2 bg-[#F4F5F7] border border-border rounded-sm p-2.5">
              <input
                id="bulk-overschrijven" type="checkbox"
                checked={bulkForm.overschrijven}
                onChange={(e) => setBulkForm({ ...bulkForm, overschrijven: e.target.checked })}
                className="w-4 h-4 accent-[#002FA7]"
                data-testid="bulk-overschrijven"
              />
              <label htmlFor="bulk-overschrijven" className="text-xs cursor-pointer">
                Bestaande dagen overschrijven (anders overslaan)
              </label>
            </div>
            <Button onClick={handleBulkSave} disabled={bulkSaving} className="bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="bulk-save">
              <Plus size={16} weight="bold" className="mr-1" />
              {bulkSaving ? "Bezig..." : "Bulk toevoegen"}
            </Button>
          </div>
        </div>
      )}

      {/* ═══ Maandrooster ═══ */}
      {mode === "rooster" && (
        <div className="bg-white border border-border rounded-sm p-5" data-testid="form-rooster">
          {/* Header filters */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
            <div>
              <Label className="text-xs font-semibold">Filiaal</Label>
              <Select value={rFiliaal || "alles"} onValueChange={(v) => { setRFiliaal(v === "alles" ? "" : v); }}>
                <SelectTrigger className="mt-1.5" data-testid="rooster-filiaal-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alles">Alle filialen</SelectItem>
                  {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Medewerker</Label>
              <Select value={rMedewerker} onValueChange={(v) => setRMedewerker(v)}>
                <SelectTrigger className="mt-1.5" data-testid="rooster-medewerker"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                <SelectContent>
                  {filiaalMedewerkers.length === 0 && andereMedewerkers.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">Geen medewerkers</div>
                  ) : (
                    <>
                      <SelectGroup>
                        {rFiliaal && (
                          <SelectLabel className="text-[10px] uppercase tracking-wider">
                            {filialen.find((f) => f.id === rFiliaal)?.naam || "Filiaal"}
                          </SelectLabel>
                        )}
                        {filiaalMedewerkers.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>
                        ))}
                      </SelectGroup>
                      {andereMedewerkers.length > 0 && (
                        <>
                          <SelectSeparator />
                          <SelectGroup>
                            <SelectLabel className="text-[10px] uppercase tracking-wider">
                              Andere medewerkers
                            </SelectLabel>
                            {andereMedewerkers.map((m) => (
                              <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>
                            ))}
                          </SelectGroup>
                        </>
                      )}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Maand</Label>
              <Select value={rMaand} onValueChange={setRMaand}>
                <SelectTrigger className="mt-1.5" data-testid="rooster-maand"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MAAND_NAMEN.map((n, i) => (<SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Jaar</Label>
              <Select value={rJaar} onValueChange={setRJaar}>
                <SelectTrigger className="mt-1.5" data-testid="rooster-jaar"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-[#F4F5F7] border border-border rounded-sm">
            <p className="text-xs text-muted-foreground flex-1">
              {standaardFiliaal ? (
                <span data-testid="rooster-auto-filiaal">
                  Uren die je invult krijgen automatisch filiaal{" "}
                  <b>{filialen.find((f) => f.id === standaardFiliaal)?.naam || ""}</b>
                </span>
              ) : rMedewerker && mwFilialen.length > 1 ? (
                <span data-testid="rooster-meerdere-filialen">
                  Deze medewerker hoort bij meerdere filialen — kies per dag zelf het filiaal
                </span>
              ) : (
                "Tip: vul eerst het hele rooster Ma-Vr automatisch in, pas daarna dagen aan."
              )}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setQuickOpen(!quickOpen)}
              data-testid="quick-fill-toggle"
            >
              <Plus size={14} weight="bold" className="mr-1" />
              Vul Ma-Vr in
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={clearRooster}
              disabled={Object.keys(rDagen).length === 0}
              data-testid="rooster-wis"
            >
              <Trash size={14} weight="bold" className="mr-1" />
              Wis scherm
            </Button>
          </div>

          {/* Quick-fill form */}
          {quickOpen && (
            <div className="mb-4 p-3 bg-[#EAF1FF] border border-[#002FA7]/30 rounded-sm" data-testid="quick-fill-panel">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px_auto] gap-2 items-end">
                <div>
                  <Label className="text-xs font-semibold">Filiaal</Label>
                  <Select value={quickFiliaal} onValueChange={setQuickFiliaal}>
                    <SelectTrigger className="mt-1 h-9" data-testid="quick-filiaal"><SelectValue placeholder="Kies..." /></SelectTrigger>
                    <SelectContent>
                      {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Uren/dag</Label>
                  <Input type="number" step="0.25" min="0" max="24" value={quickUren} onChange={(e) => setQuickUren(e.target.value)} className="mt-1 h-9" data-testid="quick-uren" />
                </div>
                <Button
                  onClick={() => { vulAllesMaVr(quickFiliaal, quickUren); setQuickOpen(false); }}
                  disabled={!quickFiliaal || !quickUren}
                  className="bg-[#002FA7] hover:bg-[#002280] text-white h-9"
                  data-testid="quick-apply"
                >
                  Toepassen
                </Button>
              </div>
            </div>
          )}

          {/* Grid */}
          <div className="overflow-x-auto border border-border rounded-sm">
            <p className="md:hidden text-[10px] text-muted-foreground px-2 pt-2 font-semibold">↔ Veeg zijwaarts voor alle kolommen</p>
            <table className="w-full text-xs min-w-[560px]">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground w-20 sticky left-0 bg-muted">Dag</th>
                  <th className="px-2 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Filialen (met uren)</th>
                  <th className="px-2 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground w-20">Totaal</th>
                </tr>
              </thead>
              <tbody>
                {dagenVanMaand.map(({ datum, dag, weekdag }) => {
                  const slots = rDagen[datum] || [];
                  const totaal = slots.reduce((s, x) => s + (parseFloat(x.uren) || 0), 0);
                  const isWeekend = weekdag >= 5;

                  // Dynamisch: toon alle gevulde slots + 1 lege (tenzij max bereikt)
                  const visibleCount = Math.min(
                    ROOSTER_SLOTS,
                    slots.length + (slots.length < ROOSTER_SLOTS ? 1 : 0),
                  );

                  return (
                    <tr
                      key={datum}
                      className={`border-t border-border ${isWeekend ? "bg-[#FFF8F0]" : ""} hover:bg-muted/30`}
                      data-testid={`rooster-row-${datum}`}
                    >
                      <td className="px-3 py-1.5 font-mono whitespace-nowrap sticky left-0 bg-inherit">
                        <span className={`font-bold ${isWeekend ? "text-[#E53E3E]" : ""}`}>{dag}</span>{" "}
                        <span className="text-muted-foreground">{DAGNAMEN_KORT[weekdag]}</span>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex flex-wrap gap-2">
                          {Array.from({ length: Math.max(1, visibleCount) }).map((_, slotIdx) => {
                            const slot = slots[slotIdx] || { filiaal_id: "", uren: "" };
                            const slotFiliaal = slot.filiaal_id || (slotIdx === 0 ? standaardFiliaal : "");
                            const filled = slot.filiaal_id && slot.uren;
                            return (
                              <div
                                key={slotIdx}
                                className="flex items-center gap-1.5 min-w-0"
                                data-testid={`rs-${datum}-${slotIdx}`}
                              >
                                <Select
                                  value={slotFiliaal}
                                  onValueChange={(v) => setSlot(datum, slotIdx, "filiaal_id", v)}
                                >
                                  <SelectTrigger
                                    className={`h-9 text-xs w-[140px] ${filled ? "border-[#002FA7]/40 bg-[#EAF1FF]" : ""}`}
                                    data-testid={`rs-${datum}-${slotIdx}-filiaal`}
                                  >
                                    <SelectValue placeholder="Filiaal..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {filialen.map((f) => (
                                      <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Input
                                  type="number" step="0.25" min="0" max="24"
                                  value={slot.uren}
                                  onChange={(e) => setSlot(datum, slotIdx, "uren", e.target.value)}
                                  placeholder="u"
                                  className={`h-9 w-20 text-sm font-semibold tabular-nums ${filled ? "bg-[#F0FAEC] border-[#1F7A1F]/40" : ""}`}
                                  data-testid={`rs-${datum}-${slotIdx}-uren`}
                                />
                                {slot.filiaal_id && (
                                  <button
                                    type="button"
                                    onClick={() => removeSlot(datum, slotIdx)}
                                    className="w-7 h-9 flex items-center justify-center text-muted-foreground hover:text-[#E53E3E]"
                                    title="Wissen"
                                    data-testid={`rs-${datum}-${slotIdx}-wis`}
                                  >
                                    <Trash size={14} weight="bold" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right font-bold tabular-nums whitespace-nowrap">
                        {totaal > 0 ? `${totaal.toFixed(2).replace(".", ",")}` : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-[#EAF1FF] border-t-2 border-[#002FA7]">
                <tr>
                  <td className="px-3 py-2.5 font-bold uppercase tracking-wider text-[#002FA7] sticky left-0 bg-[#EAF1FF]">
                    Totaal maand · {aantalDagen} dag{aantalDagen === 1 ? "" : "en"}
                  </td>
                  <td></td>
                  <td className="px-2 py-2.5 text-right font-black text-base tabular-nums text-[#002FA7]" data-testid="rooster-totaal">
                    {totaalMaand.toFixed(2).replace(".", ",")} u
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Save bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Opslaan overschrijft bestaande records voor dezelfde dag+filiaal, en voegt nieuwe toe.
            </p>
            <Button
              onClick={saveRooster}
              disabled={rSaving}
              className="bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="rooster-save"
            >
              <Plus size={16} weight="bold" className="mr-1" />
              {rSaving ? "Opslaan..." : `Maand opslaan (${MAAND_NAMEN[parseInt(rMaand) - 1]} ${rJaar})`}
            </Button>
          </div>
        </div>
      )}

      {/* ═══ Matrix (multi-medewerker × dagen) ═══ */}
      {mode === "matrix" && (
        <div className="bg-white border border-border rounded-sm p-4 sm:p-5" data-testid="form-matrix">
          {/* Periode kiezer */}
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-4">
            {/* Week vs Maand toggle */}
            <div>
              <Label className="text-xs font-semibold">Periode</Label>
              <div className="grid grid-cols-2 gap-1 p-1 bg-muted rounded-sm mt-1.5 w-44" data-testid="mx-periode-toggle">
                <button
                  onClick={() => setMxPeriode("week")}
                  className={`text-xs font-bold uppercase tracking-wider py-1.5 rounded-sm transition-colors ${
                    mxPeriode === "week" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
                  }`}
                  data-testid="mx-tab-week"
                >
                  Week
                </button>
                <button
                  onClick={() => setMxPeriode("maand")}
                  className={`text-xs font-bold uppercase tracking-wider py-1.5 rounded-sm transition-colors ${
                    mxPeriode === "maand" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
                  }`}
                  data-testid="mx-tab-maand"
                >
                  Maand
                </button>
              </div>
            </div>

            {/* Week navigatie */}
            {mxPeriode === "week" && (
              <div className="flex items-end gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setMxWeekStart((w) => shiftWeek(w, -1))}
                  data-testid="mx-week-prev"
                  title="Vorige week"
                >
                  <CaretLeft size={16} weight="bold" />
                </Button>
                <div>
                  <Label className="text-xs font-semibold">Week van</Label>
                  <Input
                    type="date"
                    value={mxWeekStart}
                    onChange={(e) => setMxWeekStart(getMondayOf(e.target.value))}
                    className="mt-1.5 w-44"
                    data-testid="mx-week-input"
                  />
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setMxWeekStart((w) => shiftWeek(w, 1))}
                  data-testid="mx-week-next"
                  title="Volgende week"
                >
                  <CaretRight size={16} weight="bold" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMxWeekStart(getMondayOf(today))}
                  data-testid="mx-week-vandaag"
                >
                  Deze week
                </Button>
              </div>
            )}

            {/* Maand navigatie */}
            {mxPeriode === "maand" && (
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs font-semibold">Maand</Label>
                  <Select value={mxMaand} onValueChange={setMxMaand}>
                    <SelectTrigger className="mt-1.5 w-36" data-testid="mx-maand"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MAAND_NAMEN.map((n, i) => (<SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Jaar</Label>
                  <Select value={mxJaar} onValueChange={setMxJaar}>
                    <SelectTrigger className="mt-1.5 w-24" data-testid="mx-jaar"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="flex-1" />

            {/* Add row dropdown */}
            {!isMedewerker && (
              <div>
                <Label className="text-xs font-semibold">+ Extra rij voor</Label>
                <Select value="" onValueChange={(v) => addMxRij(v)}>
                  <SelectTrigger className="mt-1.5 w-56" data-testid="mx-add-rij">
                    <SelectValue placeholder="Kies medewerker..." />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleMedewerkers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Info bar */}
          <div className="mb-3 p-3 bg-[#F4F5F7] border border-border rounded-sm text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{periodeLabel}</span>
            {" · "}{mxRijen.length} rij{mxRijen.length === 1 ? "" : "en"}
            {" · "}<span className="font-semibold">Tip:</span> één rij per medewerker per filiaal. Voor extra filiaal voor dezelfde medewerker → gebruik "+ Extra rij".
            {mxLoading && <span className="ml-2 italic">laden…</span>}
          </div>

          {/* Matrix grid */}
          <div className="overflow-x-auto border border-border rounded-sm">
            <p className="md:hidden text-[10px] text-muted-foreground px-2 pt-2 font-semibold">↔ Veeg zijwaarts voor alle dagen</p>
            <table className="text-xs w-full" data-testid="mx-table">
              <thead className="bg-muted">
                <tr>
                  <th className="px-2 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground sticky left-0 bg-muted z-10 min-w-[160px]">
                    Medewerker
                  </th>
                  <th className="px-2 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground sticky left-[160px] bg-muted z-10 min-w-[140px]">
                    Filiaal
                  </th>
                  {mxDagen.map(({ datum, dag, weekdag }) => {
                    const isWeekend = weekdag >= 5;
                    return (
                      <th
                        key={datum}
                        className={`px-1.5 py-2 text-center font-bold uppercase tracking-wider min-w-[60px] ${
                          isWeekend ? "text-[#E53E3E] bg-[#FFF8F0]" : "text-muted-foreground"
                        }`}
                      >
                        <div className="text-[10px]">{DAGNAMEN_KORT[weekdag]}</div>
                        <div className="text-sm">{dag}</div>
                      </th>
                    );
                  })}
                  <th className="px-2 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground sticky right-0 bg-muted z-10 min-w-[70px]">
                    Totaal
                  </th>
                  <th className="px-1 py-2 sticky right-[70px] bg-muted z-10 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {mxRijen.length === 0 ? (
                  <tr>
                    <td colSpan={mxDagen.length + 4} className="px-4 py-8 text-center text-muted-foreground">
                      Geen medewerkers. {!isMedewerker && "Voeg een rij toe via de dropdown hierboven."}
                    </td>
                  </tr>
                ) : (
                  mxRijen.map((r) => {
                    const med = medewerkers.find((m) => m.id === r.medewerker_id);
                    const medNaam = med ? `${med.voornaam} ${med.achternaam}` : "?";
                    const rijTotaal = totaalRij(r);
                    return (
                      <tr key={r.rowId} className="border-t border-border hover:bg-muted/30" data-testid={`mx-row-${r.rowId}`}>
                        <td className="px-2 py-1.5 font-semibold sticky left-0 bg-white z-[1] whitespace-nowrap">
                          {medNaam}
                        </td>
                        <td className="px-2 py-1.5 sticky left-[160px] bg-white z-[1]">
                          <Select
                            value={r.filiaal_id}
                            onValueChange={(v) => setMxFiliaal(r.rowId, v)}
                          >
                            <SelectTrigger
                              className={`h-8 text-xs ${r.filiaal_id ? "border-[#002FA7]/40 bg-[#EAF1FF]" : "border-[#E53E3E]/40 bg-[#FFF5F5]"}`}
                              data-testid={`mx-${r.rowId}-filiaal`}
                            >
                              <SelectValue placeholder="Kies..." />
                            </SelectTrigger>
                            <SelectContent>
                              {filialen.map((f) => (
                                <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        {mxDagen.map(({ datum, weekdag }) => {
                          const v = r.uren[datum] || "";
                          const isWeekend = weekdag >= 5;
                          const filled = v && parseFloat(v) > 0;
                          return (
                            <td
                              key={datum}
                              className={`px-1 py-1 text-center ${isWeekend ? "bg-[#FFF8F0]" : ""}`}
                            >
                              <input
                                type="number"
                                step="0.25"
                                min="0"
                                max="24"
                                value={v}
                                onChange={(e) => setMxCel(r.rowId, datum, e.target.value)}
                                placeholder="—"
                                className={`w-14 h-8 text-center text-xs font-semibold tabular-nums border rounded-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] ${
                                  filled
                                    ? "bg-[#F0FAEC] border-[#1F7A1F]/40 text-[#1F7A1F]"
                                    : "bg-white border-border text-muted-foreground"
                                }`}
                                data-testid={`mx-${r.rowId}-${datum}`}
                              />
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5 text-right font-bold tabular-nums sticky right-0 bg-white z-[1] whitespace-nowrap">
                          {rijTotaal > 0 ? rijTotaal.toFixed(2).replace(".", ",") : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-1 py-1.5 sticky right-[70px] bg-white z-[1]">
                          <div className="flex gap-0.5">
                            <button
                              type="button"
                              onClick={() => fillMxRowMaVr(r.rowId, "8")}
                              title="Vul Ma-Vr 8u"
                              className="w-6 h-7 text-[10px] font-bold text-[#002FA7] hover:bg-[#EAF1FF] rounded-sm"
                              data-testid={`mx-${r.rowId}-vul`}
                            >
                              8u
                            </button>
                            <button
                              type="button"
                              onClick={() => clearMxRow(r.rowId)}
                              title="Wis rij"
                              className="w-6 h-7 flex items-center justify-center text-muted-foreground hover:text-[#E53E3E]"
                              data-testid={`mx-${r.rowId}-wis`}
                            >
                              <Trash size={12} weight="bold" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeMxRij(r.rowId)}
                              title="Verwijder rij"
                              className="w-6 h-7 flex items-center justify-center text-muted-foreground hover:text-[#E53E3E] text-base"
                              data-testid={`mx-${r.rowId}-del`}
                            >
                              ×
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot className="bg-[#EAF1FF] border-t-2 border-[#002FA7]">
                <tr>
                  <td className="px-2 py-2 font-bold uppercase tracking-wider text-[#002FA7] sticky left-0 bg-[#EAF1FF] z-[1]" colSpan={2}>
                    Totaal per dag
                  </td>
                  {mxDagen.map(({ datum, weekdag }) => {
                    const t = totaalKolom(datum);
                    const isWeekend = weekdag >= 5;
                    return (
                      <td
                        key={datum}
                        className={`px-1 py-2 text-center font-bold tabular-nums text-xs ${
                          isWeekend ? "bg-[#FFF8F0]" : ""
                        } ${t > 0 ? "text-[#002FA7]" : "text-muted-foreground"}`}
                        data-testid={`mx-totaal-${datum}`}
                      >
                        {t > 0 ? t.toFixed(1).replace(".", ",") : "—"}
                      </td>
                    );
                  })}
                  <td
                    className="px-2 py-2 text-right font-black text-sm tabular-nums text-[#002FA7] sticky right-0 bg-[#EAF1FF] z-[1] whitespace-nowrap"
                    data-testid="mx-totaal"
                  >
                    {totaalMatrix.toFixed(2).replace(".", ",")} u
                  </td>
                  <td className="sticky right-[70px] bg-[#EAF1FF] z-[1]"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Save bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Opslaan overschrijft bestaande records voor dezelfde dag+medewerker+filiaal.
              Lege cellen worden genegeerd.
            </p>
            <Button
              onClick={saveMatrix}
              disabled={mxSaving || mxRijen.length === 0}
              className="bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="matrix-save"
            >
              <Plus size={16} weight="bold" className="mr-1" />
              {mxSaving ? "Opslaan..." : `Matrix opslaan (${periodeLabel})`}
            </Button>
          </div>
        </div>
      )}

      {/* Confirm extra filiaal */}
      <Dialog open={!!confirmExtra} onOpenChange={(o) => !o && setConfirmExtra(null)}>
        <DialogContent data-testid="confirm-extra-dialog">
          <DialogHeader>
            <DialogTitle>Extra filiaal toevoegen?</DialogTitle>
            <DialogDescription>
              Op deze dag ({confirmExtra?.datum}) staan al uren ingevuld voor{" "}
              een ander filiaal. Wil je toch een tweede filiaal toevoegen?
            </DialogDescription>
          </DialogHeader>
          {confirmExtra?.existing?.length > 0 && (
            <div className="bg-[#F4F5F7] border border-border rounded-sm p-3 text-xs space-y-1">
              {confirmExtra.existing.map((e, i) => (
                <div key={i} className="flex justify-between">
                  <span>{e.filiaal_naam}</span>
                  <span className="font-semibold">{e.uren.toFixed(2).replace(".", ",")} u</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-border pt-1 mt-1">
                <span className="font-semibold">Totaal al ingevuld</span>
                <span className="font-bold text-[#002FA7]">
                  {confirmExtra.existing.reduce((s, e) => s + e.uren, 0).toFixed(2).replace(".", ",")} u
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmExtra(null)}
              data-testid="confirm-extra-nee"
            >
              Nee
            </Button>
            <Button
              onClick={applyConfirmedSlot}
              className="bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="confirm-extra-ja"
            >
              Ja, toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generic confirm duplicate (1 Dag + Periode) */}
      <Dialog open={!!confirmDup} onOpenChange={(o) => !o && setConfirmDup(null)}>
        <DialogContent data-testid="confirm-dup-dialog">
          <DialogHeader>
            <DialogTitle>{confirmDup?.titel}</DialogTitle>
            <DialogDescription>{confirmDup?.intro}</DialogDescription>
          </DialogHeader>
          {confirmDup?.existing?.length > 0 && (
            <div className="bg-[#F4F5F7] border border-border rounded-sm p-3 text-xs space-y-1 max-h-[260px] overflow-y-auto">
              {confirmDup.existing.map((e, i) => (
                <div key={i} className="flex justify-between gap-3">
                  <span className="font-mono">{e.datum}</span>
                  <span className="flex-1 truncate">{e.filiaal_naam}</span>
                  <span className="font-semibold whitespace-nowrap">
                    {e.uren.toFixed(2).replace(".", ",")} u
                  </span>
                </div>
              ))}
              {confirmDup.extraCount > 0 && (
                <div className="text-muted-foreground text-center pt-1 border-t border-border">
                  + {confirmDup.extraCount} meer...
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDup(null)}
              data-testid="confirm-dup-nee"
            >
              Nee
            </Button>
            <Button
              onClick={() => {
                const cb = confirmDup?.onConfirm;
                setConfirmDup(null);
                cb && cb();
              }}
              className="bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="confirm-dup-ja"
            >
              Ja, toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
